import { FC, SyntheticEvent, useMemo } from 'react';

import { useFormik } from 'formik';
import { isEmpty, isNullish } from 'remeda';
import useSWR from 'swr';

import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { CasinoAbTestForm } from './form';

import { adsService } from 'v2/api/adsService';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { SelectOption } from 'v2/types';

interface CasinoAdFormDialogProps {
  title: string;
  initialValues: CasinoAbTestForm;
  isOpen: boolean;
  onClose: VoidFunction;
  isSubmitting: boolean;
  onSubmit: (values: CasinoAbTestForm, resetForm: VoidFunction) => void;
  isEdit?: boolean;
}

export const CasinoAbTestFormDialog: FC<CasinoAdFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
  isEdit,
}) => {
  const { data: ads } = useSWR(isOpen && 'getAds', adsService.getAds);
  const { data: casinoAds } = useSWR(isOpen && 'getCasinos', adsService.getCasinoAds);

  const formik = useFormik<CasinoAbTestForm>({
    initialValues,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
  });

  const adsOptions: SelectOption[] = useMemo(() => {
    if (isNullish(ads)) return [];

    return ads.map((a) => ({ id: a.id, value: a.id, label: a.key }));
  }, [ads]);

  const bannerOptions: SelectOption[] = useMemo(() => {
    if (isNullish(casinoAds) || isEmpty(formik.values.adId)) return [];

    return casinoAds.reduce((acc, cur) => {
      if (cur.adId === formik.values.adId) {
        acc.push({ id: cur.id, value: cur.id, label: cur.name });
      }

      return acc;
    }, [] as SelectOption[]);
  }, [casinoAds, formik.values.adId]);

  const bannerValues: SelectOption[] = useMemo(() => {
    if (isEmpty(bannerOptions)) return [];

    return bannerOptions.filter((o) => formik.values.banners.includes(o.value));
  }, [formik.values.banners, bannerOptions]);

  const handleSelectChange = (field: keyof CasinoAbTestForm) => (value: unknown) => {
    formik.setFieldValue(field, value);
  };

  const handleSelectBanners = (_: SyntheticEvent, values: SelectOption[]) => {
    formik.setFieldValue(
      'banners',
      values.map((v) => v.value)
    );
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <div className="form-group">
            <TextField
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Name"
              required
              fullWidth
            />

            <TextField
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Description"
              required
              fullWidth
            />
          </div>

          <div className="form-group">
            <Select
              value={formik.values.adId}
              options={adsOptions}
              onChange={handleSelectChange('adId')}
              label="Ad"
              disabled={isEdit}
              required
            />

            <Autocomplete
              value={bannerValues}
              options={bannerOptions}
              onChange={handleSelectBanners}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox checked={selected} />
                    {option.label}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Banners *" />}
              disableCloseOnSelect
              multiple
              fullWidth
            />
          </div>

          <TextField
            name="maxImpressions"
            value={formik.values.maxImpressions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Max Impressions"
            type="number"
            required
          />

          <div className="form-group">
            <Switch
              name="withUniqueUserImpressions"
              checked={formik.values.withUniqueUserImpressions}
              onChange={formik.handleChange}
              label="Unique Imressions"
            />

            <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button loading={isSubmitting} type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
